import {
  Add,
  Campaign,
  Close,
  Group,
  Info,
  InsertDriveFile,
  Person,
  PersonAdd,
  PersonRemove,
  MoreVert,
  Search,
  Shield,
  Send,
  AttachFile,
  Support,
} from "@mui/icons-material";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { queryKeys } from "../../hooks/queries/queryKeys";
import { useChatUsers, useGroup } from "../../hooks/queries/useChat";
import { useSocket } from "../../hooks/useSocket";
import { usePermissions } from "../../hooks/usePermissions";
import Toast from "../../components/common/Toast";
import { store } from "../../store";
import chatService from "../../services/chatService";
import { formatChatMessagePreview } from "../../utils/chatMessagePreview";
import {
  addGroupMembers,
  createConversation,
  createGroup,
  fetchConversations,
  fetchGroupMessages,
  fetchMessages,
  removeGroupMember,
  sendGroupMessage,
  sendMessage,
  setActiveConversation,
  ensureSupportConversation,
  addOptimisticMessage,
} from "../../store/slices/chatSlice";

type AppDispatch = typeof store.dispatch;

interface ChatParticipant {
  id: number;
  name?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  profileImage?: string;
  role?: string;
  groupRole?: "admin" | "member";
  isGroupAdmin?: boolean;
}

interface Conversation {
  id: number;
  type: "individual" | "group";
  name?: string;
  avatar?: string;
  lastMessage?: string;
  time?: string;
  unread?: number;
  members?: number;
  participants?: ChatParticipant[];
  /** True for platform → superadmin support threads (use conversation message API, not ChatGroup). */
  isSupportRoom?: boolean;
  /** True for institute broadcast channels (read-only or post per permissions). */
  isBroadcastRoom?: boolean;
  /** False when the logged-in user was removed but can still view prior messages. */
  isActiveMember?: boolean;
}

interface Message {
  id: number | string;
  clientMessageId?: string;
  conversationId?: number | string;
  sender?: string;
  senderId?: number;
  content: string;
  type?: "text" | "file" | "image";
  fileUrl?: string;
  time?: string;
  isOwn: boolean;
  isSystem?: boolean;
  status?: "sending" | "sent" | "failed";
}

interface SystemEvent {
  event: string;
  removedUserId?: number;
  removedUserName?: string;
  removedByUserId?: number;
  removedByUserName?: string;
  leftUserId?: number;
  leftUserName?: string;
  addedUserId?: number;
  addedUserIds?: number[];
  addedUserName?: string;
  addedByUserName?: string;
}

const parseSystemMessage = (content: string): SystemEvent | null => {
  if (!content?.startsWith("[SYSTEM]")) return null;
  try {
    return JSON.parse(content.slice(8)) as SystemEvent;
  } catch {
    return null;
  }
};

const formatSystemMessage = (event: SystemEvent): string => {
  if (event.event === "member_removed" && event.removedUserName && event.removedByUserName) {
    return `${event.removedUserName} was removed by ${event.removedByUserName}`;
  }
  if (event.event === "member_left" && event.leftUserName) {
    return `${event.leftUserName} left the group`;
  }
  if (event.event === "member_added" && event.addedUserName && event.addedByUserName) {
    return `${event.addedUserName} was added by ${event.addedByUserName}`;
  }
  return event.removedUserName ? `${event.removedUserName} was removed` : "A member was removed";
};

interface ChatUser {
  id: number;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  role?: string;
  profileImage?: string;
}

interface ChatState {
  conversations: Conversation[];
  messages: Message[];
  loading: boolean;
  messagesLoading: boolean;
  onlineUsers: string[];
}

const getDisplayName = (user: ChatUser): string =>
  user.fullName ||
  `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
  "User";

/** Community groups use /chat/groups APIs; support/broadcast threads are `type: "group"` but differ by flags. */
const isBroadcastChat = (c: Conversation | null | undefined): boolean =>
  !!c && c.type === "group" && c.isBroadcastRoom === true;

const isCommunityGroupChat = (c: Conversation | null | undefined): boolean =>
  !!c && c.type === "group" && c.isSupportRoom !== true && c.isBroadcastRoom !== true;

const WHATSAPP = {
  chatListBg: "#111b21",
  chatListHeader: "#202c33",
  chatAreaBg: "#efeae2",
  outgoingBubble: "#d9fdd3",
  incomingBubble: "#ffffff",
  headerBg: "#202c33",
  textPrimary: "#e9edef",
  textSecondary: "#8696a0",
  border: "#2a3942",
  hover: "#2a3942",
  selected: "#2a3942",
};

const CHAT_SKELETON_SX = {
  bgcolor: "rgba(233, 237, 239, 0.14)",
  "&::after": {
    background:
      "linear-gradient(90deg, transparent, rgba(233, 237, 239, 0.22), transparent)",
  },
};

const SUPPORT_CHAT_BUTTON_SX = {
  mt: 0.5,
  textTransform: "none" as const,
  fontWeight: 600,
  borderRadius: "8px",
  bgcolor: "#00a884",
  color: "#ffffff",
  border: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: "#008f72",
    boxShadow: "none",
  },
  "&:disabled": {
    bgcolor: "rgba(0, 168, 132, 0.45)",
    color: "#ffffff",
  },
  "& .MuiButton-startIcon": {
    color: "#ffffff",
  },
};

const SUPPORT_MENU_ITEM_SX = {
  color: "#008069",
  fontWeight: 500,
  "&:hover": { bgcolor: "rgba(0, 168, 132, 0.1)" },
};

const createClientMessageId = () =>
  `temp_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const Chat = () => {
  const dispatch = useDispatch<AppDispatch>() as (action: unknown) => unknown;
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const {
    conversations = [],
    messages = [],
    loading,
    messagesLoading,
    onlineUsers = [],
  } = useSelector((state: { chat: ChatState }) => state.chat);
  const authUser = useSelector((state: { auth?: { user?: Record<string, unknown> } }) => state.auth?.user);
  const { hasVisiblePermission, hasPermission } = usePermissions();

  const {
    connected,
    typingUsers,
    joinConversation,
    leaveConversation,
    emitTyping,
    emitStopTyping,
    markRead,
  } = useSocket();

  const [activeTab, setActiveTab] = useState(0);
  const [selectedChat, setSelectedChat] = useState<Conversation | null>(null);
  const [message, setMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);

  const [groupName, setGroupName] = useState("");
  const [groupMemberSearch, setGroupMemberSearch] = useState("");
  const [selectedMembers, setSelectedMembers] = useState<ChatUser[]>([]);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [groupDetailsOpen, setGroupDetailsOpen] = useState(false);
  const [addMembersOpen, setAddMembersOpen] = useState(false);
  const [addMembersSearch, setAddMembersSearch] = useState("");
  const [debouncedAddMembersSearch, setDebouncedAddMembersSearch] = useState("");
  const [pendingMemberIds, setPendingMemberIds] = useState<Set<string>>(new Set());
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [adminActionMemberId, setAdminActionMemberId] = useState<string | null>(null);
  const [memberMenuAnchor, setMemberMenuAnchor] = useState<null | HTMLElement>(null);
  const [memberMenuTarget, setMemberMenuTarget] = useState<{ id: number; isGroupAdmin?: boolean } | null>(null);
  const isViewerSuperAdmin = useMemo(() => {
    const roleFrom = (u: Record<string, unknown> | null | undefined) => {
      if (!u || typeof u !== "object") return "";
      const r = u.role as Record<string, unknown> | string | undefined;
      if (r && typeof r === "object" && "slug" in r) return String((r as { slug?: string }).slug || "");
      return String(r || "");
    };
    let slug = roleFrom(authUser as Record<string, unknown>);
    if (!slug) {
      try {
        const u = JSON.parse(localStorage.getItem("user") || "{}");
        slug = roleFrom(u);
      } catch {
        /* ignore */
      }
    }
    if (!slug) {
      try {
        const token = localStorage.getItem("accessToken");
        if (token) {
          const payload = JSON.parse(atob(token.split(".")[1] || "e30="));
          slug = String(payload?.role || payload?.userRole || payload?.roleName || "");
        }
      } catch {
        /* ignore */
      }
    }
    const r = slug.toLowerCase().replace(/\s+/g, "");
    return r === "superadmin";
  }, [authUser]);
  const canPlatformSupportChat = hasVisiblePermission("support.chat");
  const canStartDirectChat = hasPermission("chat.one_to_one");
  const canCreateGroupChat = hasPermission("chat.group");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDownloadFile = (url: string, fileName?: string) => {
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.download = fileName || "file";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const { data: searchResults = [], isLoading: searchLoading } = useChatUsers(
    userSearch,
    newChatOpen
  );
  const { data: groupMemberResults = [], isLoading: groupMemberLoading } =
    useChatUsers(groupMemberSearch, createGroupOpen);
  const { data: groupDetails, isLoading: groupDetailsLoading } = useGroup(
    groupDetailsOpen && isCommunityGroupChat(selectedChat) ? selectedChat.id : null
  );
  useEffect(() => {
    if (!addMembersOpen) {
      setDebouncedAddMembersSearch("");
      return;
    }
    const t = setTimeout(() => setDebouncedAddMembersSearch(addMembersSearch), 400);
    return () => clearTimeout(t);
  }, [addMembersSearch, addMembersOpen]);
  const { data: addMembersResults = [] } = useChatUsers(debouncedAddMembersSearch, addMembersOpen);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** chatSlice is JS; thunk accepts optional `{ silent: true }` to avoid sidebar skeleton flicker */
  const refreshConversations = useCallback(
    (silent = true) => {
      // @ts-expect-error JS chatSlice thunk accepts optional { silent }
      dispatch(fetchConversations({ silent }));
    },
    [dispatch]
  );

  const getCurrentUserId = (): number | null => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) return null;
      const payload = JSON.parse(atob(token.split(".")[1] || "e30="));
      return payload?.id ?? null;
    } catch {
      return null;
    }
  };

  const { isCurrentUserRemoved, visibleMessages } = useMemo(() => {
    const currentUserId = getCurrentUserId();
    if (!selectedChat || selectedChat.type !== "group" || !currentUserId) {
      return { isCurrentUserRemoved: false, visibleMessages: messages };
    }

    if (selectedChat.isActiveMember === false) {
      const isLeaveEvent = (msg: Message) => {
        const ev = parseSystemMessage(msg.content);
        return (
          (ev?.event === "member_removed" && ev.removedUserId === currentUserId) ||
          (ev?.event === "member_left" && ev.leftUserId === currentUserId)
        );
      };
      let lastLeaveIndex = -1;
      for (let i = messages.length - 1; i >= 0; i--) {
        if (isLeaveEvent(messages[i])) {
          lastLeaveIndex = i;
          break;
        }
      }
      if (lastLeaveIndex >= 0) {
        return {
          isCurrentUserRemoved: true,
          visibleMessages: messages.slice(0, lastLeaveIndex + 1),
        };
      }
      return { isCurrentUserRemoved: true, visibleMessages: messages };
    }

    const isActiveMember = (selectedChat.participants || []).some(
      (p) => Number(p.id) === Number(currentUserId)
    );
    if (isActiveMember) {
      return { isCurrentUserRemoved: false, visibleMessages: messages };
    }

    const isLeaveEvent = (msg: Message) => {
      const ev = parseSystemMessage(msg.content);
      return (
        (ev?.event === "member_removed" && ev.removedUserId === currentUserId) ||
        (ev?.event === "member_left" && ev.leftUserId === currentUserId)
      );
    };
    const isRejoinEvent = (msg: Message) => {
      const ev = parseSystemMessage(msg.content);
      if (ev?.event !== "member_added") return false;
      const addedIds = ev.addedUserIds || (ev.addedUserId != null ? [ev.addedUserId] : []);
      return addedIds.some((id) => Number(id) === Number(currentUserId));
    };

    let lastLeaveIndex = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (isLeaveEvent(messages[i])) {
        lastLeaveIndex = i;
        break;
      }
    }
    if (lastLeaveIndex === -1) {
      return { isCurrentUserRemoved: false, visibleMessages: messages };
    }

    const wasReadded = messages
      .slice(lastLeaveIndex + 1)
      .some((msg) => isRejoinEvent(msg));
    if (wasReadded) {
      return { isCurrentUserRemoved: false, visibleMessages: messages };
    }

    return {
      isCurrentUserRemoved: true,
      visibleMessages: messages.slice(0, lastLeaveIndex + 1),
    };
  }, [selectedChat, messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [visibleMessages]);

  useEffect(() => {
    if (!newChatOpen) setUserSearch("");
  }, [newChatOpen]);

  useEffect(() => {
    if (!createGroupOpen) {
      setGroupName("");
      setGroupMemberSearch("");
      setSelectedMembers([]);
    }
  }, [createGroupOpen]);

  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: [...queryKeys.chat.all, "users"] });
    };
  }, [queryClient]);

  const handleSelectChat = useCallback(
    (chat: Conversation) => {
      if (chat?.id == null || !Number.isFinite(Number(chat.id))) return;
      if ((location.state as { conversationId?: string | number } | null)?.conversationId != null) {
        navigate("/chat", { replace: true, state: null });
      }
      if (selectedChat?.id) {
        leaveConversation(selectedChat.id);
      }
      setSelectedChat(chat);
      dispatch(setActiveConversation(chat.id));
      if (isCommunityGroupChat(chat)) {
        // @ts-expect-error - chatSlice is JS, fetchGroupMessages expects { groupId, params? }
        dispatch(fetchGroupMessages({ groupId: chat.id }));
      } else {
        // @ts-expect-error - chatSlice is JS, fetchMessages expects { conversationId, params? }
        dispatch(fetchMessages({ conversationId: chat.id }));
      }
      joinConversation(chat.id);
      markRead(chat.id);
    },
    [dispatch, joinConversation, leaveConversation, markRead, selectedChat, location.state, navigate]
  );

  useEffect(() => {
    const raw = conversationId || (location.state as { conversationId?: string | number } | null)?.conversationId;
    const id = raw == null ? null : String(raw);
    if (!id || !conversations.length) return;
    if (selectedChat?.id != null && String(selectedChat.id) === id) return;
    const conv = conversations.find((c) => String(c.id) === id);
    if (conv) {
      handleSelectChat(conv);
      if ((location.state as { conversationId?: string | number } | null)?.conversationId != null) {
        navigate("/chat", { replace: true, state: null });
      }
    }
  }, [conversationId, location.state, conversations, selectedChat?.id, handleSelectChat, navigate]);

  useEffect(() => {
    if (!selectedChat?.id || !conversations.length) return;
    const latest = conversations.find((c) => String(c.id) === String(selectedChat.id));
    if (!latest) return;
    const latestParticipantIds = (latest.participants || []).map((p) => p.id).sort().join(",");
    const selectedParticipantIds = (selectedChat.participants || []).map((p) => p.id).sort().join(",");
    if (
      latestParticipantIds !== selectedParticipantIds ||
      latest.name !== selectedChat.name ||
      latest.members !== selectedChat.members ||
      latest.isActiveMember !== selectedChat.isActiveMember
    ) {
      setSelectedChat((prev) => (prev ? { ...prev, ...latest } : latest));
    }
  }, [conversations, selectedChat?.id, selectedChat?.name, selectedChat?.members, selectedChat?.participants, selectedChat?.isActiveMember]);

  const handleSendMessage = () => {
    if ((message.trim() || selectedFile) && selectedChat && !isCurrentUserRemoved) {
      const content = message.trim() || (selectedFile ? selectedFile.name : "");
      const file = selectedFile;
      const clientMessageId = createClientMessageId();
      const userId = getCurrentUserId();
      const isImage = file?.type?.startsWith("image/");
      const optimisticType = file ? (isImage ? "image" : "file") : "text";
      const previewUrl = file && isImage ? URL.createObjectURL(file) : undefined;

      dispatch(
        addOptimisticMessage({
          id: clientMessageId,
          clientMessageId,
          conversationId: selectedChat.id,
          content,
          type: optimisticType,
          fileUrl: previewUrl,
          time: new Date().toISOString(),
          senderId: userId ?? undefined,
          isOwn: true,
          status: "sending",
        })
      );

      setMessage("");
      removeSelectedFile();
      emitStopTyping(selectedChat.id);

      const messageData = { content: message.trim(), file };

      if (isCommunityGroupChat(selectedChat)) {
        dispatch(
          // @ts-expect-error JS chatSlice — optimistic send with clientMessageId
          sendGroupMessage({
            groupId: selectedChat.id,
            message: messageData,
            clientMessageId,
          })
        );
      } else {
        dispatch(
          // @ts-expect-error JS chatSlice — optimistic send with clientMessageId
          sendMessage({
            conversationId: selectedChat.id,
            message: messageData,
            clientMessageId,
          })
        );
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleTyping = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);
    if (selectedChat) {
      emitTyping(selectedChat.id);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        emitStopTyping(selectedChat.id);
      }, 2000);
    }
  };

  const formatTime = (dateStr?: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const oneDay = 86400000;
    if (diff < oneDay && date.getDate() === now.getDate()) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    if (diff < 2 * oneDay) return "Yesterday";
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  const isUserOnline = (chat: Conversation): boolean => {
    if (chat.type !== "individual" || !chat.participants) return false;
    const currentUserId = getCurrentUserId();
    const other = chat.participants?.find((p) => p.id !== currentUserId);
    if (!other) return false;
    return onlineUsers.some((id) => String(id) === String(other.id));
  };

  const filteredChats = conversations.filter((chat) => {
    const matchesSearch = (chat.name || "")
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    if (activeTab === 0) return matchesSearch;
    if (activeTab === 1) return matchesSearch && chat.type === "individual";
    if (activeTab === 2) {
      return matchesSearch && chat.type === "group" && !chat.isBroadcastRoom && !chat.isSupportRoom;
    }
    if (activeTab === 3) return matchesSearch && isBroadcastChat(chat);
    return matchesSearch;
  });

  const broadcastUnread = useMemo(
    () => conversations.filter((c) => isBroadcastChat(c)).reduce((sum, c) => sum + (c.unread || 0), 0),
    [conversations]
  );

  const handleSearchUsers = (query: string) => setUserSearch(query);

  const handleStartConversation = async (user: ChatUser) => {
    setNewChatOpen(false);
    setUserSearch("");
    // @ts-expect-error - chatSlice is JS, createConversation expects participantId
    const result = await dispatch(createConversation(user.id));
    if ((result as { payload?: Conversation }).payload) {
      refreshConversations(true);
      const conv = (result as { payload: Conversation }).payload;
      handleSelectChat({
        id: conv.id,
        type: conv.type || "individual",
        name: getDisplayName(user),
        participants: conv.participants?.map((p) => p as ChatParticipant) || [],
      });
    }
  };

  const handleAddMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAddMenuAnchor(e.currentTarget);
  };

  const handleAddMenuClose = () => setAddMenuAnchor(null);

  const handleOpenNewChat = () => {
    handleAddMenuClose();
    setNewChatOpen(true);
  };

  const handleOpenCreateGroup = () => {
    handleAddMenuClose();
    setCreateGroupOpen(true);
  };

  const [supportOpening, setSupportOpening] = useState(false);

  const handleChatWithSupportTeam = async () => {
    handleAddMenuClose();
    setSupportOpening(true);
    try {
    const result = (await dispatch(ensureSupportConversation())) as {
      type?: string;
      payload?: unknown;
    };
    if (result.type?.endsWith("/rejected")) {
      setToast({
        open: true,
        message: String(result.payload || "Could not open support chat"),
        severity: "error",
      });
      return;
    }
    const conv = result.payload as {
      id: number;
      participants?: Array<{ userId?: number; user?: ChatUser }>;
    };
    if (!conv?.id) {
      setToast({ open: true, message: "Could not open support chat", severity: "error" });
      return;
    }
    const participants: ChatParticipant[] = (conv.participants || [])
      .map((p) => {
        const u = p.user;
        const id = (u?.id ?? p.userId) as number;
        return {
          id,
          firstName: u?.firstName,
          lastName: u?.lastName,
          fullName: u?.fullName,
          name: u ? getDisplayName(u) : "User",
          profileImage: u?.profileImage,
          role: u?.role,
        };
      })
      .filter((x) => x.id != null);
    handleSelectChat({
      id: conv.id,
      type: "group",
      isSupportRoom: true,
      name: "Support team",
      members: participants.length,
      participants,
    });
    } finally {
      setSupportOpening(false);
    }
  };

  const handleAddMember = (user: ChatUser) => {
    if (!selectedMembers.some((m) => m.id === user.id)) {
      setSelectedMembers((prev) => [...prev, user]);
    }
  };

  const handleRemoveMember = (userId: number) => {
    setSelectedMembers((prev) => prev.filter((m) => m.id !== userId));
  };

  const handleCreateGroup = async () => {
    const name = groupName.trim();
    if (!name) return;
    const memberIds = selectedMembers.map((m) => m.id);
    try {
      // @ts-expect-error - chatSlice is JS, createGroup expects { name, memberIds }
      const result = await dispatch(createGroup({ name, memberIds }));
      const payload = (result as { payload?: { id: number; name: string; members: number } })
        .payload;
      const rejected = (result as { type?: string }).type?.endsWith("/rejected");
      if (rejected) {
        const errMsg = (result as { payload?: string }).payload || "Failed to create group";
        setToast({ open: true, message: errMsg, severity: "error" });
        return;
      }
      if (payload) {
        refreshConversations(true);
        setCreateGroupOpen(false);
        setGroupName("");
        setSelectedMembers([]);
        setToast({ open: true, message: "Group created successfully", severity: "success" });
        handleSelectChat({
          id: payload.id,
          type: "group",
          name: payload.name,
          members: payload.members,
        });
      }
    } catch (err) {
      setToast({ open: true, message: "Failed to create group", severity: "error" });
    }
  };

  const typingConvId = selectedChat?.id;
  const isOtherTyping =
    typingConvId && typingUsers[typingConvId as keyof typeof typingUsers];

  const handleOpenGroupDetails = () => {
    if (isCommunityGroupChat(selectedChat)) setGroupDetailsOpen(true);
  };

  const handleCloseGroupDetails = () => {
    setGroupDetailsOpen(false);
    setAddMembersOpen(false);
    setAddMembersSearch("");
    setPendingMemberIds(new Set());
    setRemovingMemberId(null);
  };

  const handleAddMembersToGroup = async (user: ChatUser) => {
    if (!selectedChat || !isCommunityGroupChat(selectedChat)) return;
    try {
      setPendingMemberIds((s) => {
        const n = new Set(s);
        n.add(String(user.id));
        return n;
      });
      // @ts-expect-error - chatSlice is JS
      await dispatch(addGroupMembers({ groupId: selectedChat.id, memberIds: [user.id] }));
      setToast({ open: true, message: "Member added", severity: "success" });
      setAddMembersOpen(false);
      setAddMembersSearch("");
      queryClient.invalidateQueries({ queryKey: [...queryKeys.chat.groups(), selectedChat.id] });
      refreshConversations(true);
      refreshGroupDetails();
      setSelectedChat((prev) =>
        prev && prev.id === selectedChat.id
          ? { ...prev, members: (prev.members || 0) + 1 }
          : prev
      );
    } catch {
      setPendingMemberIds((s) => {
        const n = new Set(s);
        n.delete(String(user.id));
        return n;
      });
      setToast({ open: true, message: "Failed to add member", severity: "error" });
    }
  };

  const handleRemoveMemberFromGroup = async (memberId: number | string) => {
    if (!selectedChat || !isCommunityGroupChat(selectedChat)) return;
    const key = String(memberId);
    setRemovingMemberId(key);
    try {
      // @ts-expect-error - chatSlice is JS
      await dispatch(removeGroupMember({ groupId: selectedChat.id, memberId: Number(memberId) }));
      setToast({ open: true, message: "Member removed", severity: "success" });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.chat.groups(), selectedChat.id] });
      refreshConversations(true);
      // @ts-expect-error - chatSlice is JS, fetchGroupMessages expects { groupId, params? }
      dispatch(fetchGroupMessages({ groupId: selectedChat.id }));
      setGroupDetailsOpen(false);
      setSelectedChat((prev) =>
        prev && prev.id === selectedChat.id
          ? { ...prev, members: Math.max(0, (prev.members || 1) - 1) }
          : prev
      );
    } catch {
      setToast({ open: true, message: "Failed to remove member", severity: "error" });
    } finally {
      setRemovingMemberId(null);
    }
  };

  const refreshGroupDetails = () => {
    if (!selectedChat?.id) return;
    queryClient.invalidateQueries({ queryKey: [...queryKeys.chat.groups(), selectedChat.id] });
  };

  const handleOpenMemberMenu = (event: React.MouseEvent<HTMLElement>, member: { id: number; isGroupAdmin?: boolean }) => {
    event.stopPropagation();
    setMemberMenuAnchor(event.currentTarget);
    setMemberMenuTarget(member);
  };

  const handleCloseMemberMenu = () => {
    setMemberMenuAnchor(null);
    setMemberMenuTarget(null);
  };

  const handlePromoteMemberAdmin = async (memberId: number | string) => {
    if (!selectedChat || !isCommunityGroupChat(selectedChat)) return;
    const key = String(memberId);
    setAdminActionMemberId(key);
    handleCloseMemberMenu();
    try {
      await chatService.promoteGroupMemberAdmin(selectedChat.id, Number(memberId));
      setToast({ open: true, message: "Member is now a group admin", severity: "success" });
      refreshGroupDetails();
    } catch {
      setToast({ open: true, message: "Failed to make admin", severity: "error" });
    } finally {
      setAdminActionMemberId(null);
    }
  };

  const handleRevokeMemberAdmin = async (memberId: number | string) => {
    if (!selectedChat || !isCommunityGroupChat(selectedChat)) return;
    const key = String(memberId);
    setAdminActionMemberId(key);
    handleCloseMemberMenu();
    try {
      await chatService.revokeGroupMemberAdmin(selectedChat.id, Number(memberId));
      setToast({ open: true, message: "Admin privileges removed", severity: "success" });
      refreshGroupDetails();
    } catch {
      setToast({ open: true, message: "Failed to remove admin", severity: "error" });
    } finally {
      setAdminActionMemberId(null);
    }
  };

  const normalizedGroupMembers = useMemo(() => {
    const toArr = (x: any) => Array.isArray(x) ? x : Array.isArray(x?.data) ? x.data : Array.isArray(x?.rows) ? x.rows : Array.isArray(x?.items) ? x.items : [];
    const gd: any = groupDetails || {};
    const d: any = gd?.data || gd?.result || gd?.payload || {};
    const list: any[] =
      toArr(gd.participants).length ? toArr(gd.participants) :
      toArr(gd.members).length ? toArr(gd.members) :
      toArr(gd.users).length ? toArr(gd.users) :
      toArr(gd.groupMembers).length ? toArr(gd.groupMembers) :
      toArr(gd.group?.participants).length ? toArr(gd.group?.participants) :
      toArr(gd.group?.members).length ? toArr(gd.group?.members) :
      toArr(d.participants).length ? toArr(d.participants) :
      toArr(d.members).length ? toArr(d.members) :
      toArr(d.users).length ? toArr(d.users) :
      toArr(d.groupMembers).length ? toArr(d.groupMembers) :
      toArr(d.group?.participants).length ? toArr(d.group?.participants) :
      toArr(d.group?.members).length ? toArr(d.group?.members) : [];
    return (Array.isArray(list) ? list : []).map((p: any) => {
      const u = p?.user || p?.member || p;
      const id = u?.id ?? p?.userId ?? p?.user_id ?? p?.memberId ?? p?.member_id ?? p?.id;
      const isGroupAdmin =
        p?.isGroupAdmin === true ||
        p?.groupRole === "admin" ||
        u?.isGroupAdmin === true ||
        u?.groupRole === "admin";
      return { ...(u || {}), id, isGroupAdmin, groupRole: isGroupAdmin ? "admin" : "member" };
    }).filter((m) => m?.id != null);
  }, [groupDetails]);

  const viewerIsGroupAdmin = useMemo(() => {
    const uid = getCurrentUserId();
    if (uid == null) return false;
    const gd = groupDetails as { viewerIsGroupAdmin?: boolean; createdBy?: number } | undefined;
    if (gd?.viewerIsGroupAdmin === true) return true;
    if (gd?.createdBy != null && Number(gd.createdBy) === Number(uid)) return true;
    return normalizedGroupMembers.some(
      (m) => String(m.id) === String(uid) && m.isGroupAdmin === true
    );
  }, [groupDetails, normalizedGroupMembers]);

  const groupMemberIdSet = useMemo(() => new Set(normalizedGroupMembers.map((m) => String(m.id))), [normalizedGroupMembers]);
  const excludedMemberIdSet = useMemo(() => {
    const s = new Set(groupMemberIdSet);
    pendingMemberIds.forEach((id) => s.add(id));
    return s;
  }, [groupMemberIdSet, pendingMemberIds]);

  return (
    <Box
      sx={{
        height: "calc(100vh - 64px)",
        display: "flex",
        overflow: "hidden",
        borderRadius: 0,
      }}
    >
      {/* Chat List - WhatsApp style dark sidebar */}
      <Box
        sx={{
          width: 380,
          minWidth: 320,
          display: "flex",
          flexDirection: "column",
          bgcolor: WHATSAPP.chatListBg,
          borderRight: "1px solid",
          borderColor: WHATSAPP.border,
        }}
      >
        <Box
          sx={{
            p: 2,
            bgcolor: WHATSAPP.chatListHeader,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: WHATSAPP.textPrimary }}>
              Messages
            </Typography>
            {(canStartDirectChat || canCreateGroupChat) && (
            <IconButton
              size="small"
              onClick={handleAddMenuOpen}
              sx={{ color: WHATSAPP.textSecondary }}
              aria-controls={addMenuAnchor ? "add-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={!!addMenuAnchor}
            >
              <Add />
            </IconButton>
            )}
            <Menu
              id="add-menu"
              anchorEl={addMenuAnchor}
              open={!!addMenuAnchor}
              onClose={handleAddMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              {canStartDirectChat && (
              <MenuItem onClick={handleOpenNewChat}>
                <Person sx={{ mr: 1, fontSize: 20 }} />
                New Conversation
              </MenuItem>
              )}
              {!isViewerSuperAdmin && canPlatformSupportChat && (
                <MenuItem
                  onClick={() => void handleChatWithSupportTeam()}
                  disabled={supportOpening}
                  sx={SUPPORT_MENU_ITEM_SX}
                >
                  {supportOpening ? (
                    <CircularProgress size={18} sx={{ mr: 1, color: "#00a884" }} />
                  ) : (
                    <Support sx={{ mr: 1, fontSize: 20, color: "#00a884" }} />
                  )}
                  {supportOpening ? "Opening support chat…" : "Chat with support team"}
                </MenuItem>
              )}
              {canCreateGroupChat && (
                <MenuItem onClick={handleOpenCreateGroup}>
                  <Group sx={{ mr: 1, fontSize: 20 }} />
                  Create Group
                </MenuItem>
              )}
            </Menu>
          </Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: WHATSAPP.chatListBg,
                borderRadius: "8px",
                "& fieldset": { borderColor: "transparent" },
                "&:hover fieldset": { borderColor: "transparent" },
                "&.Mui-focused fieldset": { borderColor: "transparent" },
              },
              "& .MuiInputBase-input": { color: WHATSAPP.textPrimary },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: WHATSAPP.textSecondary, fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          {!isViewerSuperAdmin && canPlatformSupportChat && (
            <Button
              fullWidth
              size="small"
              variant="contained"
              disableElevation
              disabled={supportOpening}
              startIcon={
                supportOpening ? (
                  <CircularProgress size={16} sx={{ color: "#ffffff" }} />
                ) : (
                  <Support sx={{ fontSize: 18, color: "#ffffff" }} />
                )
              }
              onClick={() => void handleChatWithSupportTeam()}
              sx={SUPPORT_CHAT_BUTTON_SX}
            >
              {supportOpening ? "Opening support chat…" : "Chat with support team"}
            </Button>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: connected ? "#25d366" : "#8696a0",
                boxShadow: connected ? "0 0 8px #25d366" : "none",
              }}
            />
            <Typography variant="caption" sx={{ color: WHATSAPP.textSecondary }}>
              {connected ? "Real-time connected" : "Connecting..."}
            </Typography>
          </Box>
        </Box>

        <Tabs
          value={activeTab}
          onChange={(_, v: number) => setActiveTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 40,
            borderBottom: "1px solid",
            borderColor: WHATSAPP.border,
            "& .MuiTab-root": { color: WHATSAPP.textSecondary, minHeight: 40 },
            "& .Mui-selected": { color: "primary.main" },
          }}
        >
          <Tab label="All" />
          <Tab icon={<Person sx={{ fontSize: 18 }} />} iconPosition="start" aria-label="Direct chats" />
          <Tab icon={<Group sx={{ fontSize: 18 }} />} iconPosition="start" aria-label="Group chats" />
          <Tab
            icon={
              <Badge badgeContent={broadcastUnread} color="error" max={99} invisible={broadcastUnread <= 0}>
                <Campaign sx={{ fontSize: 18 }} />
              </Badge>
            }
            iconPosition="start"
            aria-label="Broadcasts"
          />
        </Tabs>

        <List sx={{ flexGrow: 1, overflow: "auto", py: 0 }}>
          {loading ? (
            [...Array(5)].map((_, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2,
                  py: 1.5,
                }}
              >
                <Skeleton variant="circular" width={40} height={40} sx={CHAT_SKELETON_SX} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="60%" sx={CHAT_SKELETON_SX} />
                  <Skeleton variant="text" width="80%" height={14} sx={CHAT_SKELETON_SX} />
                </Box>
              </Box>
            ))
          ) : filteredChats.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4, px: 2 }}>
              <Typography variant="body2" sx={{ color: WHATSAPP.textSecondary }}>
                {searchQuery ? "No conversations found" : "No conversations yet"}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center", mt: 2 }}>
                {activeTab === 3 ? (
                  <Typography variant="caption" sx={{ color: WHATSAPP.textSecondary }}>
                    Broadcast channels appear here when your institute publishes them.
                  </Typography>
                ) : activeTab === 2 ? (
                  <>
                    {canCreateGroupChat && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<Group />}
                      onClick={handleOpenCreateGroup}
                      sx={{ bgcolor: "#00a884", "&:hover": { bgcolor: "#008f72" } }}
                    >
                      Create Group
                    </Button>
                    )}
                    {canStartDirectChat && (
                    <Button
                      size="small"
                      startIcon={<Person />}
                      onClick={handleOpenNewChat}
                      sx={{ color: WHATSAPP.textSecondary }}
                    >
                      New Conversation
                    </Button>
                    )}
                  </>
                ) : (
                  canStartDirectChat ? (
                  <Button
                    size="small"
                    startIcon={<Add />}
                    onClick={handleOpenNewChat}
                    sx={{ color: WHATSAPP.textSecondary }}
                  >
                    Start a chat
                  </Button>
                  ) : null
                )}
              </Box>
            </Box>
          ) : (
            filteredChats.map((chat) => {
              const online = isUserOnline(chat);
              const isSelected = selectedChat?.id === chat.id;
              return (
                <ListItemButton
                  key={chat.id}
                  selected={isSelected}
                  onClick={() => handleSelectChat(chat)}
                  sx={{
                    py: 1.5,
                    borderBottom: "1px solid",
                    borderColor: WHATSAPP.border,
                    bgcolor: isSelected ? WHATSAPP.selected : "transparent",
                    "&:hover": { bgcolor: WHATSAPP.hover },
                    "&.Mui-selected": { bgcolor: WHATSAPP.selected },
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 48, mr: 2 }}>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      badgeContent={
                        online ? (
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              bgcolor: "#25d366",
                              borderRadius: "50%",
                              border: "2px solid #111b21",
                            }}
                          />
                        ) : null
                      }
                    >
                      <Avatar
                        src={chat.avatar || undefined}
                        sx={{
                          bgcolor:
                            chat.type === "group"
                              ? chat.isSupportRoom
                                ? "#5c6bc0"
                                : chat.isBroadcastRoom
                                  ? "#1976d2"
                                  : "#00a884"
                              : "#6a7175",
                          width: 48,
                          height: 48,
                        }}
                      >
                        {chat.type === "group" ? (
                          chat.isSupportRoom ? (
                            <Support />
                          ) : chat.isBroadcastRoom ? (
                            <Campaign />
                          ) : (
                            <Group />
                          )
                        ) : (
                          (chat.name || "?")[0]
                        )}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={(chat.unread ?? 0) > 0 ? 600 : 400}
                          noWrap
                          sx={{ color: WHATSAPP.textPrimary }}
                        >
                          {chat.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: WHATSAPP.textSecondary, flexShrink: 0, ml: 0.5 }}>
                          {formatTime(chat.time)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            maxWidth: 180,
                            fontWeight: (chat.unread ?? 0) > 0 ? 500 : 400,
                            color: WHATSAPP.textSecondary,
                          }}
                        >
                          {formatChatMessagePreview(chat.lastMessage || "") || "No messages yet"}
                        </Typography>
                        {(chat.unread ?? 0) > 0 && (
                          <Box
                            sx={{
                              minWidth: 20,
                              height: 20,
                              borderRadius: "50%",
                              bgcolor: "#00a884",
                              color: "white",
                              fontSize: 11,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {chat.unread}
                          </Box>
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              );
            })
          )}
        </List>
      </Box>

      {/* Chat Window - WhatsApp style */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          bgcolor: selectedChat ? WHATSAPP.chatAreaBg : WHATSAPP.chatListBg,
          minWidth: 0,
        }}
      >
        {selectedChat ? (
          <>
            <Box
              sx={{
                p: 1.5,
                px: 2,
                bgcolor: WHATSAPP.chatListHeader,
                display: "flex",
                alignItems: "center",
                gap: 2,
                borderBottom: "1px solid",
                borderColor: WHATSAPP.border,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2.5,
                  cursor: isCommunityGroupChat(selectedChat) ? "pointer" : "default",
                  flex: 1,
                  minWidth: 0,
                }}
                onClick={isCommunityGroupChat(selectedChat) ? handleOpenGroupDetails : undefined}
              >
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  badgeContent={
                    isUserOnline(selectedChat) ? (
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          bgcolor: "#25d366",
                          borderRadius: "50%",
                          border: "2px solid #202c33",
                        }}
                      />
                    ) : null
                  }
                >
                  <Avatar
                    src={selectedChat.avatar || undefined}
                    sx={{ bgcolor: "#00a884", width: 40, height: 40 }}
                  >
                    {selectedChat.type === "group" ? (
                      selectedChat.isSupportRoom ? (
                        <Support />
                      ) : (
                        <Group />
                      )
                    ) : (
                      (selectedChat.name || "?")[0]
                    )}
                  </Avatar>
                </Badge>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ color: WHATSAPP.textPrimary }} noWrap>
                    {selectedChat.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: WHATSAPP.textSecondary }}>
                    {isOtherTyping
                      ? "typing..."
                      : selectedChat.type === "group"
                        ? `${selectedChat.members ?? normalizedGroupMembers.length ?? 0} members`
                        : isUserOnline(selectedChat)
                          ? "Online"
                          : "Offline"}
                  </Typography>
                </Box>
                {isCommunityGroupChat(selectedChat) && (
                  <IconButton size="small" onClick={handleOpenGroupDetails} title="Group details" sx={{ color: WHATSAPP.textSecondary }}>
                    <Info />
                  </IconButton>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                flexGrow: 1,
                overflow: "auto",
                p: 2,
                bgcolor: WHATSAPP.chatAreaBg,
                backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d9dbd5' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
              }}
            >
              {messagesLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={32} sx={{ color: "#00a884" }} />
                </Box>
              ) : visibleMessages.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography color="text.secondary">
                    No messages yet. Say hello!
                  </Typography>
                </Box>
              ) : (
                visibleMessages.map((msg) => {
                  const systemEvent = parseSystemMessage(msg.content);
                  const isSystem = msg.isSystem || !!systemEvent;

                  if (isSystem && systemEvent) {
                    return (
                      <Box
                        key={msg.id}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          my: 1.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            px: 2,
                            py: 0.75,
                            borderRadius: 1,
                            bgcolor: "rgba(0,0,0,0.08)",
                            color: "#667781",
                            maxWidth: "85%",
                            textAlign: "center",
                            fontSize: 12,
                          }}
                        >
                          {formatSystemMessage(systemEvent)}
                        </Typography>
                      </Box>
                    );
                  }

                  const currentUserId = getCurrentUserId();
                  const isOwn = msg.isOwn || (currentUserId && msg.senderId === currentUserId);

                  return (
                    <Box
                      key={msg.id}
                      sx={{
                        display: "flex",
                        justifyContent: isOwn ? "flex-end" : "flex-start",
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: "65%",
                          px: 1.5,
                          py: 0.75,
                          pt: msg.sender && !msg.isOwn && selectedChat.type === "group" ? 0.5 : 1,
                          borderRadius: "8px",
                          borderTopRightRadius: isOwn ? 2 : 8,
                          borderTopLeftRadius: isOwn ? 8 : 2,
                          bgcolor: isOwn ? WHATSAPP.outgoingBubble : WHATSAPP.incomingBubble,
                          color: "#111b21",
                          boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
                          opacity: msg.status === "sending" ? 0.72 : msg.status === "failed" ? 0.85 : 1,
                        }}
                      >
                        {!msg.isOwn &&
                          selectedChat.type === "group" &&
                          msg.sender && (
                            <Typography
                              variant="caption"
                              sx={{ color: "#00a884", fontWeight: 600, fontSize: 12.5 }}
                            >
                              {msg.sender}
                            </Typography>
                          )}
                        {msg.type === "image" && msg.fileUrl && (
                          <Box
                            component="img"
                            src={msg.fileUrl}
                            alt="Sent image"
                            sx={{
                              width: "100%",
                              maxWidth: 300,
                              maxHeight: 300,
                              borderRadius: 1,
                              mb: 0.5,
                              cursor: "pointer",
                              display: "block",
                              objectFit: "contain",
                              bgcolor: "rgba(0,0,0,0.05)",
                            }}
                            onClick={() => handleDownloadFile(msg.fileUrl!, "image")}
                          />
                        )}
                        {msg.type === "file" && msg.fileUrl && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              p: 1,
                              mb: 0.5,
                              borderRadius: 1,
                              bgcolor: "rgba(0,0,0,0.05)",
                              cursor: "pointer",
                            }}
                            onClick={() => handleDownloadFile(msg.fileUrl!, msg.content)}
                          >
                            <InsertDriveFile sx={{ color: "#8696a0" }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" noWrap sx={{ fontSize: 13.5 }}>
                                {msg.content || "Attachment"}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        {/* Only show content as text if it's not a redundant filename for a file/image */}
                        {msg.type === "text" && (
                          <Typography variant="body2" sx={{ fontSize: 14.2, lineHeight: 1.4 }}>
                            {msg.content}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            textAlign: "right",
                            mt: 0.25,
                            fontSize: 11,
                            color: msg.isOwn ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0.4)",
                          }}
                        >
                          {msg.status === "failed"
                            ? "Failed to send"
                            : msg.status === "sending"
                              ? "Sending…"
                              : formatTime(msg.time)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </Box>

            {isCurrentUserRemoved ? (
              <Box
                sx={{
                  p: 2,
                  textAlign: "center",
                  bgcolor: "rgba(0,0,0,0.06)",
                  borderTop: "1px solid",
                  borderColor: "#e9edef",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  You can&apos;t send messages to this group because you&apos;re no longer a participant.
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  p: 1.5,
                  px: 2,
                  bgcolor: "#f0f2f5",
                  borderTop: "1px solid",
                  borderColor: "#e9edef",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    bgcolor: "white",
                    borderRadius: "8px",
                    px: 2,
                    py: 1,
                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                  }}
                >
                  {selectedFile && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        pb: 1,
                        borderBottom: "1px solid",
                        borderColor: "#e9edef",
                      }}
                    >
                      {filePreview ? (
                        <Box
                          component="img"
                          src={filePreview}
                          sx={{
                            width: 60,
                            height: 60,
                            borderRadius: 1,
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <InsertDriveFile sx={{ color: "#8696a0", fontSize: 40 }} />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ color: "#111b21" }}>
                          {selectedFile.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#8696a0" }}>
                          {(selectedFile.size / 1024).toFixed(1)} KB
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={removeSelectedFile}>
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                  <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      onChange={handleFileSelect}
                    />
                    <IconButton
                      size="small"
                      onClick={() => fileInputRef.current?.click()}
                      sx={{ color: "#8696a0", mb: 0.5 }}
                    >
                      <AttachFile />
                    </IconButton>
                    <TextField
                      fullWidth
                      multiline
                      maxRows={4}
                      placeholder="Type a message"
                      value={message}
                      onChange={handleTyping}
                      onKeyPress={handleKeyPress}
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        sx: { fontSize: 15 },
                      }}
                      sx={{ flex: 1 }}
                    />
                    <IconButton
                      onClick={handleSendMessage}
                      disabled={!message.trim() && !selectedFile}
                      sx={{
                        color: (message.trim() || selectedFile) ? "#00a884" : "#8696a0",
                        "&:hover": { bgcolor: "rgba(0,168,132,0.1)" },
                      }}
                    >
                      <Send />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            )}
          </>
        ) : (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 2,
              p: 4,
            }}
          >
            <Box
              sx={{
                width: 320,
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  width: 250,
                  height: 250,
                  mx: "auto",
                  mb: 2,
                  borderRadius: "50%",
                  bgcolor: WHATSAPP.chatListHeader,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Send sx={{ fontSize: 96, color: WHATSAPP.border }} />
              </Box>
              <Typography variant="h5" fontWeight={300} sx={{ color: WHATSAPP.textPrimary, mb: 1 }}>
                ERP Messages
              </Typography>
              <Typography variant="body2" sx={{ color: WHATSAPP.textSecondary, mb: 3 }}>
                Send and receive messages in real time. Start a new conversation or create a group to collaborate with your team.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center" }}>
                {canStartDirectChat && (
                <Button
                  variant="contained"
                  startIcon={<Person />}
                  onClick={handleOpenNewChat}
                  sx={{ bgcolor: "#00a884", "&:hover": { bgcolor: "#008f72" } }}
                >
                  New chat
                </Button>
                )}
                {canCreateGroupChat && (
                <Button
                  variant="outlined"
                  startIcon={<Group />}
                  onClick={handleOpenCreateGroup}
                  sx={{
                    borderColor: WHATSAPP.textSecondary,
                    color: WHATSAPP.textSecondary,
                    "&:hover": { borderColor: WHATSAPP.textPrimary, color: WHATSAPP.textPrimary },
                  }}
                >
                  Create group
                </Button>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
      />

      {/* New Chat Dialog */}
      <Dialog
        open={newChatOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setNewChatOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          New Conversation
          <IconButton size="small" onClick={() => setNewChatOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search users by name..."
            value={userSearch}
            onChange={(e) => handleSearchUsers(e.target.value)}
            sx={{ mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <List sx={{ maxHeight: 300, overflow: "auto", mt: 1 }}>
            {searchLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : searchResults.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", py: 2 }}
              >
                {userSearch.length >= 2
                  ? "No users found"
                  : "No users available"}
              </Typography>
            ) : (
              (searchResults as ChatUser[]).map((user) => {
                const displayName = getDisplayName(user);
                return (
                  <ListItemButton
                    key={user.id}
                    onClick={() => handleStartConversation(user)}
                  >
                    <ListItemAvatar sx={{ mr: 2 }}>
                      <Avatar src={user.profileImage || undefined}>
                        {displayName[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={displayName}
                      secondary={user.role || user.email}
                    />
                  </ListItemButton>
                );
              })
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewChatOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Create Group Dialog */}
      <Dialog
        open={createGroupOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setCreateGroupOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Create Group
          <IconButton size="small" onClick={() => setCreateGroupOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Group name"
            placeholder="Enter group name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth
            size="small"
            placeholder="Search users to add..."
            value={groupMemberSearch}
            onChange={(e) => setGroupMemberSearch(e.target.value)}
            sx={{ mt: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          {selectedMembers.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
              {selectedMembers.map((m) => (
                <Chip
                  key={m.id}
                  label={getDisplayName(m)}
                  size="small"
                  onDelete={() => handleRemoveMember(m.id)}
                />
              ))}
            </Box>
          )}
          <List sx={{ maxHeight: 220, overflow: "auto", mt: 1 }}>
            {groupMemberLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (groupMemberResults as ChatUser[]).length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", py: 2 }}
              >
                {groupMemberSearch.length >= 2
                  ? "No users found"
                  : "No users available. Type at least 2 characters to search."}
              </Typography>
            ) : (
              (groupMemberResults as ChatUser[])
                .filter((u) => !selectedMembers.some((m) => m.id === u.id))
                .map((user) => {
                  const displayName = getDisplayName(user);
                  return (
                    <ListItemButton
                      key={user.id}
                      onClick={() => handleAddMember(user)}
                    >
                      <ListItemAvatar sx={{ mr: 2 }}>
                        <Avatar src={user.profileImage || undefined}>
                          {displayName[0]}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={displayName}
                        secondary={user.role || user.email}
                      />
                    </ListItemButton>
                  );
                })
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateGroupOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateGroup}
            disabled={!groupName.trim()}
          >
            Create Group
          </Button>
        </DialogActions>
      </Dialog>

      {/* Group Details Dialog */}
      <Dialog
        open={groupDetailsOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleCloseGroupDetails();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Group Details
          <IconButton size="small" onClick={handleCloseGroupDetails}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {groupDetailsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Avatar sx={{ bgcolor: "primary.main", width: 56, height: 56 }}>
                  <Group sx={{ fontSize: 32 }} />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {groupDetails?.name || selectedChat?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(normalizedGroupMembers.length || selectedChat?.members || 0)} members
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Members
                </Typography>
                {viewerIsGroupAdmin && (
                  <Button
                    size="small"
                    startIcon={<PersonAdd />}
                    onClick={() => setAddMembersOpen(true)}
                  >
                    Add members
                  </Button>
                )}
              </Box>
              <List sx={{ maxHeight: 280, overflow: "auto" }}>
                {normalizedGroupMembers.map((member) => {
                  const name = getDisplayName(member);
                  const isCurrentUser = String(member.id) === String(getCurrentUserId());
                  const isAdmin = member.isGroupAdmin === true;
                  const showMemberActions = viewerIsGroupAdmin && !isCurrentUser;
                  return (
                    <ListItemButton
                      key={member.id}
                      sx={{ py: 1.5 }}
                      disableRipple={isCurrentUser}
                    >
                      <ListItemAvatar sx={{ mr: 2 }}>
                        <Avatar src={member.profileImage || undefined}>
                          {name[0]}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Typography variant="body2">{name}</Typography>
                            {isCurrentUser && (
                              <Chip label="You" size="small" sx={{ height: 18, fontSize: 10 }} />
                            )}
                            {isAdmin && (
                              <Chip
                                icon={<Shield sx={{ fontSize: "14px !important" }} />}
                                label="Admin"
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ height: 20, fontSize: 10, "& .MuiChip-icon": { ml: 0.5 } }}
                              />
                            )}
                          </Box>
                        }
                        secondary={member.role || member.email}
                      />
                      {showMemberActions && (
                        <IconButton
                          size="small"
                          disabled={removingMemberId !== null || adminActionMemberId !== null}
                          onClick={(e) => handleOpenMemberMenu(e, { id: Number(member.id), isGroupAdmin: isAdmin })}
                          title="Member actions"
                        >
                          {adminActionMemberId === String(member.id) ? (
                            <CircularProgress size={18} />
                          ) : (
                            <MoreVert fontSize="small" />
                          )}
                        </IconButton>
                      )}
                    </ListItemButton>
                  );
                })}
              </List>
              <Menu
                anchorEl={memberMenuAnchor}
                open={Boolean(memberMenuAnchor)}
                onClose={handleCloseMemberMenu}
              >
                {memberMenuTarget && !memberMenuTarget.isGroupAdmin && (
                  <MenuItem onClick={() => handlePromoteMemberAdmin(memberMenuTarget.id)}>
                    Make Admin
                  </MenuItem>
                )}
                {memberMenuTarget?.isGroupAdmin && (
                  <MenuItem onClick={() => handleRevokeMemberAdmin(memberMenuTarget.id)}>
                    Remove Admin
                  </MenuItem>
                )}
                {memberMenuTarget && (
                  <MenuItem
                    onClick={() => {
                      handleCloseMemberMenu();
                      handleRemoveMemberFromGroup(memberMenuTarget.id);
                    }}
                    sx={{ color: "error.main" }}
                  >
                    Remove from group
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Members to Group Dialog */}
      <Dialog
        open={addMembersOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setAddMembersOpen(false);
          setAddMembersSearch("");
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Add members
          <IconButton size="small" onClick={() => { setAddMembersOpen(false); setAddMembersSearch(""); }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search users to add..."
            value={addMembersSearch}
            onChange={(e) => setAddMembersSearch(e.target.value)}
            sx={{ mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <List sx={{ maxHeight: 300, overflow: "auto", mt: 1 }}>
            {(addMembersResults as ChatUser[])
              .filter((u) => !excludedMemberIdSet.has(String(u.id)))
              .map((user) => {
                const displayName = getDisplayName(user);
                return (
                  <ListItemButton
                    key={user.id}
                    onClick={() => handleAddMembersToGroup(user)}
                  >
                    <ListItemAvatar sx={{ mr: 2 }}>
                      <Avatar src={user.profileImage || undefined}>
                        {displayName[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={displayName}
                      secondary={user.role || user.email}
                    />
                    <IconButton size="small" color="primary" title="Add">
                      <PersonAdd />
                    </IconButton>
                  </ListItemButton>
                );
              })}
          </List>
          {debouncedAddMembersSearch.length >= 2 &&
            (addMembersResults as ChatUser[]).filter((u) => !excludedMemberIdSet.has(String(u.id))).length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                No users found or all are already in the group
              </Typography>
            )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Chat;
